/**
 * The "thumbnail slider" product gallery layout shows one image at a time
 * by setting display:none on every slide except the active one (see
 * .product--thumbnail_slider .product__media-item:not(.is-active) in
 * section-main-product.css), rather than laying every slide out side by
 * side and scrolling between them. Dawn's own SliderComponent (global.js)
 * only knows the second approach — its previous/next buttons move the
 * gallery's scrollLeft by one slide's width, which does nothing here since
 * a display:none slide never contributes any width to scroll past.
 *
 * Clicking a thumbnail directly still works correctly, because that goes
 * through a separate mechanism (media-gallery.js) that swaps the
 * "is-active" class directly rather than scrolling. Rather than touch the
 * display:none layout choice or Dawn's own slider code, this makes the
 * main image's own previous/next buttons drive that same, already-working
 * thumbnail click — same result (the image changes, and the thumbnail
 * strip highlights and scrolls to match), reached through the one path
 * that was already reliable. It also gives those buttons the same
 * fade-when-nothing-further disabled state the thumbnail strip's own
 * buttons already have, which the broken scroll-based logic never
 * produced for the main image since it bails out before getting there.
 */
(function () {
  'use strict';

  /**
   * Dawn's SliderComponent (global.js) recalculates its own scroll-based
   * bookkeeping — sliderItemsToShow, sliderItemOffset, totalPages, and a
   * "slideChanged" event it dispatches off the back of them — every time
   * a ResizeObserver sees its slider element's size change, on both the
   * main image and the thumbnail strip alike (both are slider-component
   * elements; see the top of this file for why that bookkeeping never
   * meant anything for either one here in the first place). Confirmed
   * directly on the live theme: because only one slide/thumbnail ever
   * actually has width at a time in this display:none-swap layout,
   * sliderItemsToShow ends up with a single entry, making
   * sliderItemOffset — the difference between two items' offsetLeft —
   * compute as 0. Dividing by that in update() produces NaN, which
   * "slideChanged" then carries as its currentPage; media-gallery.js's
   * own listener for that event (onSlideChanged) crashes trying to read
   * a property off the resulting undefined slide. This gallery's own
   * animateSlideChange below briefly resizes both the main image and,
   * through it, the thumbnail column beneath it, which is enough on its
   * own to trigger this every time a slide changes.
   *
   * This hasn't been pinned down as the actual cause of the mobile-only
   * photo-reverts-to-the-first-one bug this project is chasing — that
   * crash happens the same way on desktop, and onSlideChanged itself
   * only ever touches the thumbnail strip's highlight, never which photo
   * is showing — but it's a confirmed, real crash firing on a hot path
   * (every slide change) regardless, for bookkeeping neither gallery
   * here still needs: every button, swipe, and thumbnail click is
   * already driven directly elsewhere in this file. Removing a live,
   * firing crash from that path is worth doing on its own even without
   * proof it's the whole explanation. This turns initPages() into a
   * no-op for both, which starves update() (and so "slideChanged") of
   * ever running at all. this.initPages() is looked up on the prototype
   * fresh on every call — by the constructor once, and by that
   * ResizeObserver every time after — rather than captured once up
   * front, so patching the prototype here reaches every future call
   * regardless of how long each instance has already existed.
   */
  var sliderComponentCtor = customElements.get('slider-component');
  if (sliderComponentCtor) {
    var originalInitPages = sliderComponentCtor.prototype.initPages;
    sliderComponentCtor.prototype.initPages = function () {
      if (this.classList.contains('GalleryViewerMain') || this.classList.contains('GalleryThumbnails')) return;
      return originalInitPages.apply(this, arguments);
    };
  }

  function slidesFor(gallery) {
    return Array.from(gallery.querySelectorAll('.product__media-item[id^="Slide-"]'));
  }

  function activeIndex(slides) {
    return slides.findIndex(function (slide) { return slide.classList.contains('is-active'); });
  }

  /**
   * A brief orange flash (see .wattec-bounce in section-slider.css) for
   * pressing a button that's genuinely got nowhere further to go, so the
   * press still visibly registers instead of the button just sitting
   * there looking like it didn't work. Restarting the CSS animation on an
   * element that's mid-flash needs the class removed and its layout
   * re-read before adding it back, or the browser just no-ops the second
   * add.
   */
  function bounce(button) {
    button.classList.remove('wattec-bounce');
    void button.offsetWidth;
    button.classList.add('wattec-bounce');
    setTimeout(function () { button.classList.remove('wattec-bounce'); }, 400);
  }

  /**
   * The thumbnail strip's own version of the same feedback: a glow
   * flashing in from the edge of the last thumbnail itself — top/bottom
   * on the desktop column, left/right on the mobile/tablet row — rather
   * than on the button, since that reads better here (see
   * .wattec-bounce-top/-bottom/-start/-end in section-slider.css).
   */
  function bounceEdge(gallery, direction) {
    var vertical = window.innerWidth >= 1025;
    var cls = vertical
      ? (direction === 'next' ? 'wattec-bounce-bottom' : 'wattec-bounce-top')
      : (direction === 'next' ? 'wattec-bounce-end' : 'wattec-bounce-start');
    gallery.classList.remove('wattec-bounce-top', 'wattec-bounce-bottom', 'wattec-bounce-start', 'wattec-bounce-end');
    void gallery.offsetWidth;
    gallery.classList.add(cls);
    setTimeout(function () { gallery.classList.remove(cls); }, 550);
  }

  /**
   * The main image's own version of the same "pressed/swiped, but
   * nothing there" feedback — a glow along its own left or right edge
   * (see .GalleryViewerMain::before/::after in section-slider.css)
   * rather than top/bottom or start/end, since the main image is always
   * side-by-side left/right, on mobile and desktop alike.
   */
  function bounceImageEdge(gallery, direction) {
    var cls = direction === 'next' ? 'wattec-bounce-right' : 'wattec-bounce-left';
    gallery.classList.remove('wattec-bounce-left', 'wattec-bounce-right');
    void gallery.offsetWidth;
    gallery.classList.add(cls);
    setTimeout(function () { gallery.classList.remove(cls); }, 550);
  }

  /**
   * A swipe's release, on both mobile and desktop, naturally fires a
   * plain click afterwards the same way any press-and-release does — and
   * on a phone, a horizontal swipe across the main image can easily end
   * with the finger having drifted slightly down onto the thumbnail row
   * sitting right below it. That stray click lands on whichever
   * thumbnail happens to be under it (often one of the first, since
   * that's what's still in view early on) and, being a perfectly normal
   * thumbnail click as far as the browser or media-gallery.js can tell,
   * switches the photo again immediately after the swipe already
   * switched it correctly — seen as the photo animating to the swiped-to
   * image and then immediately jumping back. Suppressing the single
   * click that follows a swipe everywhere on the page, not just within
   * the gallery itself, closes that off regardless of exactly where it
   * lands, the same way a browser already swallows the synthetic click
   * that follows a touch-drag on a native scroller.
   */
  //
  // Only ever the ONE real click that follows the release, and never a
  // click this file (or the theme) makes itself: an earlier version
  // swallowed every click for half a second, which also ate a genuine
  // press of the arrows made quickly after a swipe — seen as the arrows
  // sometimes "not responding".
  var suppressClicksUntil = 0;
  function suppressNextClickGlobally(ms) {
    suppressClicksUntil = Date.now() + (ms || 400);
  }
  document.addEventListener('click', function (event) {
    if (!event.isTrusted) return;
    if (Date.now() < suppressClicksUntil) {
      suppressClicksUntil = 0;
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  /**
   * Tracks which photo was actually, deliberately chosen last, per
   * gallery — either a real tap (event.isTrusted, set below) or this
   * file's own deliberate navigation (button press or swipe, marked
   * directly at the call site in goToAdjacentSlide). watchGalleries'
   * revert guard (below) uses this to tell a genuine choice of photo
   * apart from something else silently moving is-active on its own: both
   * look identical as a plain class change, but only a real choice ever
   * updates this record for that exact photo. Tracking *which* photo
   * rather than just *when* the last click happened matters — an early
   * version of this only recorded a timestamp and treated anything
   * within 200ms of it as trusted, which meant a misfire landing that
   * soon after a genuine tap on a *different* photo still slipped
   * through unnoticed.
   *
   * event.isTrusted is what makes this actually catch the bug it was
   * built for — a since-identified case of the Aris/Avis product-options
   * app, installed on this store for its own swatch options, quietly
   * calling .click() on this exact same first thumbnail's image whenever
   * it tries and fails to find a photo whose alt text matches an
   * option's value (which none of these photos' alt text ever will).
   * That call is indistinguishable from a real tap by target or by
   * result — both land on the same button and both fire a real click
   * event — but never by isTrusted, which the browser sets to false for
   * absolutely anything dispatched by script rather than an actual
   * press, and that included this app's call the same as it would this
   * file's own.
   */
  var lastDeliberateMediaId = new WeakMap();
  function markDeliberateChoice(gallery, mediaId) {
    lastDeliberateMediaId.set(gallery, mediaId);
  }
  document.addEventListener('click', function (event) {
    if (!event.isTrusted) return;
    var thumbnailButton = event.target.closest('.GalleryThumbnails button.thumbnail');
    if (!thumbnailButton) return;
    var item = thumbnailButton.closest('[data-target]');
    var mediaGallery = thumbnailButton.closest('media-gallery');
    var viewer = mediaGallery && mediaGallery.querySelector('.GalleryViewerMain, slider-component[id^="GalleryViewer-"]');
    if (viewer && item) markDeliberateChoice(viewer, item.dataset.target);
  }, true);

  // Returns whether it actually moved to another slide, so callers can
  // tell a real press-with-nowhere-to-go apart from a normal move — the
  // edge glow above only makes sense for the former.
  function goToAdjacentSlide(gallery, direction) {
    var slides = slidesFor(gallery);
    var current = activeIndex(slides);
    if (current === -1) return false;
    var target = direction === 'next' ? current + 1 : current - 1;
    if (target < 0 || target >= slides.length) return false;
    var mediaId = slides[target].dataset.mediaId;
    if (!mediaId) return false;
    var thumbnailButton = document.querySelector('.thumbnail-list__item[data-target="' + mediaId + '"] button.thumbnail');
    if (!thumbnailButton) return false;
    // This click is synthetic too (same as the app misfire this guards
    // against), but it's this file's own deliberate navigation — a
    // button press or a completed swipe — so it's marked as a real
    // choice directly here rather than relying on isTrusted, which
    // would be false for this one exactly as it is for the app's.
    markDeliberateChoice(gallery, mediaId);
    thumbnailButton.click();
    return true;
  }

  /**
   * data-atlimit stands in for the disabled attribute here on purpose: a
   * genuinely disabled button can't be pressed at all, which is exactly
   * what stops the "pressed, but nothing there" bounce above from ever
   * being reachable. This still fades the button the same way (see
   * section-slider.css) but leaves it clickable so that flash can happen.
   */
  function updateButtonState(gallery) {
    var slides = slidesFor(gallery);
    if (!slides.length) return;
    var current = activeIndex(slides);
    var prevBtn = gallery.querySelector('button[name="previous"]');
    var nextBtn = gallery.querySelector('button[name="next"]');
    if (prevBtn) prevBtn.toggleAttribute('data-atlimit', current <= 0);
    if (nextBtn) nextBtn.toggleAttribute('data-atlimit', current === -1 || current >= slides.length - 1);
  }

  // Delegated so it keeps working after a variant change re-renders the
  // gallery markup, rather than needing to re-attach listeners each time.
  document.addEventListener('click', function (event) {
    var button = event.target.closest(
      '.GalleryViewerMain button[name="previous"], .GalleryViewerMain button[name="next"], ' +
      'slider-component[id^="GalleryViewer-"] button[name="previous"], slider-component[id^="GalleryViewer-"] button[name="next"]'
    );
    if (!button) return;
    var gallery = button.closest('slider-component');
    if (!gallery) return;
    var direction = button.name === 'next' ? 'next' : 'prev';
    if (button.hasAttribute('data-atlimit')) { bounceImageEdge(gallery, direction); return; }
    goToAdjacentSlide(gallery, direction);
  });

  /**
   * A sliding transition for the main photo, whichever way it's changed —
   * the previous/next buttons, a thumbnail click, or a swipe all end the
   * same way (the "is-active" class moving to a different slide), so
   * rather than build this into each of those separately, watchGalleries
   * below just notices that class move and animates between whichever two
   * slides were involved. Slides are otherwise shown one at a time via
   * display:none (see the top of this file for why), so for the moment
   * of the animation both are put back on screen, side by side, and slid
   * across; normal styles take back over once it's done.
   *
   * Three things the first version of this got wrong, each seen as the
   * photo "distorting" or the arrows "not responding":
   *
   * - A second change arriving mid-slide (a quick double press) started
   *   a second animation on top of the first, and the first one's clean-up
   *   then wiped the styles the second was still using. Now whatever is
   *   running is finished off instantly before the next one starts.
   * - It ended on the first "transitionend" to reach the slide — which
   *   bubbles up from anything inside it, such as a hover effect on the
   *   photo's tags — so it could be cut off half way. It now ends on its
   *   own timer.
   * - Both photos were squeezed into the height of the new one and the
   *   frame then snapped. Each photo now keeps its own proportions and the
   *   frame's height glides from the old photo's to the new one's.
   *
   * A swipe that has already dragged the photo part of the way (see
   * watchSwipe) hands over its position in gallery.__wattecDrag, and the
   * slide carries on from exactly there rather than restarting.
   */
  var SLIDE_EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

  function mediaListFor(gallery) {
    return gallery.querySelector('.product__media-list');
  }

  function clearSlideStyles(slide) {
    if (!slide) return;
    ['transition', 'display', 'position', 'top', 'left', 'width', 'zIndex', 'transform', 'willChange'].forEach(function (prop) {
      slide.style[prop] = '';
    });
  }

  function finishSlideAnimation(gallery) {
    if (gallery.__wattecFinishAnim) gallery.__wattecFinishAnim();
  }

  function settleGallery(gallery) {
    var container = mediaListFor(gallery);
    if (container) gallery.__wattecHeight = container.getBoundingClientRect().height;
    gallery.__wattecAnimating = false;
    (gallery.__wattecOnSettle || []).forEach(function (fn) { fn(); });
  }

  function onGallerySettle(gallery, fn) {
    (gallery.__wattecOnSettle = gallery.__wattecOnSettle || []).push(fn);
  }

  function positionForSlide(slide, display) {
    slide.style.transition = 'none';
    slide.style.display = display;
    slide.style.position = 'absolute';
    slide.style.top = '0';
    slide.style.left = '0';
    slide.style.width = '100%';
    slide.style.zIndex = '1';
    slide.style.willChange = 'transform';
  }

  function animateSlideChange(gallery, fromSlide, toSlide, direction) {
    var container = mediaListFor(gallery);
    var drag = gallery.__wattecDrag || null;
    gallery.__wattecDrag = null;
    if (!container || !fromSlide || !toSlide || fromSlide === toSlide) {
      if (drag) drag.release();
      return;
    }
    // A drag in progress has its own finisher; anything else still
    // running is completed on the spot.
    if (!drag) finishSlideAnimation(gallery);

    var width = container.getBoundingClientRect().width;
    // toSlide already has is-active, so its computed display is the right
    // "shown" value at this screen width ("flex" on a phone, "list-item"
    // on desktop).
    var shownDisplay = drag ? drag.display : getComputedStyle(toSlide).display;
    var newHeight = toSlide.getBoundingClientRect().height;
    var oldHeight = drag ? drag.height : (gallery.__wattecHeight || newHeight);
    var startX = drag ? drag.offset : 0;
    if (drag && drag.neighbour && drag.neighbour !== toSlide) clearSlideStyles(drag.neighbour);

    positionForSlide(fromSlide, shownDisplay);
    positionForSlide(toSlide, shownDisplay);
    var enterFrom = direction === 'next' ? width : -width;
    fromSlide.style.transform = 'translate3d(' + startX + 'px,0,0)';
    toSlide.style.transform = 'translate3d(' + (enterFrom + startX) + 'px,0,0)';
    container.style.position = 'relative';
    container.style.overflow = 'hidden';
    container.style.transition = 'none';
    container.style.height = oldHeight + 'px';
    gallery.__wattecAnimating = true;

    // Commit the starting positions before the transition is switched on.
    void toSlide.offsetWidth;

    // A drag that already covered most of the way only has the rest to go.
    var remaining = width ? Math.max(0.35, 1 - Math.abs(startX) / width) : 1;
    var duration = Math.round(380 * remaining);
    var transition = 'transform ' + duration + 'ms ' + SLIDE_EASE;
    fromSlide.style.transition = transition;
    toSlide.style.transition = transition;
    container.style.transition = 'height ' + duration + 'ms ' + SLIDE_EASE;
    fromSlide.style.transform = 'translate3d(' + (-enterFrom) + 'px,0,0)';
    toSlide.style.transform = 'translate3d(0,0,0)';
    container.style.height = newHeight + 'px';

    var done = false;
    var timer;
    function finish() {
      if (done) return;
      done = true;
      clearTimeout(timer);
      gallery.__wattecFinishAnim = null;
      clearSlideStyles(fromSlide);
      clearSlideStyles(toSlide);
      container.style.transition = '';
      container.style.position = '';
      container.style.overflow = '';
      container.style.height = '';
      settleGallery(gallery);
    }
    gallery.__wattecFinishAnim = finish;
    timer = setTimeout(finish, duration + 40);
  }

  function watchGalleries() {
    document.querySelectorAll('.GalleryViewerMain, slider-component[id^="GalleryViewer-"]').forEach(function (gallery) {
      updateButtonState(gallery);
      settleGallery(gallery);
      // The first photo's height changes as it loads and when the window
      // is resized; keep the "height to slide from" current between
      // changes (never mid-slide, when the frame's height is our own).
      if (window.ResizeObserver) {
        new ResizeObserver(function () {
          if (!gallery.__wattecAnimating && !gallery.__wattecDragging) {
            var list = mediaListFor(gallery);
            if (list) gallery.__wattecHeight = list.getBoundingClientRect().height;
          }
        }).observe(gallery);
      }
      var slides = slidesFor(gallery);
      var lastIndex = activeIndex(slides);
      var lastSlide = slides[lastIndex] || null;
      // Reported on Android Chrome and Brave, and in the theme editor's
      // own mobile preview: tapping a thumbnail correctly shows that
      // photo and highlights the thumbnail, then moments later the photo
      // alone jumps back to the very first one. Traced to a genuine
      // outside cause rather than anything in this gallery's own code —
      // this store's Aris/Avis product-options app ships a
      // "change the gallery image to match this option" feature for its
      // own swatches, and its fallback logic, when it can't find a photo
      // whose alt text matches the option's value (never going to match
      // for photos captioned normally rather than with a swatch value),
      // is to click the *first* thumbnail's image, unconditionally —
      // confirmed directly by reading that app's own script. That lands
      // on the exact same button a real tap on the first photo would, so
      // from this file's own point of view it looks identical to the
      // customer choosing that photo themselves.
      //
      // This only guards that one specific shape — is-active landing on
      // the very first slide without that photo actually having been
      // the one chosen — rather than any change that wasn't preceded by
      // a click at all. Scoping it that narrowly matters: this same app
      // feature may work correctly elsewhere (an option whose value DOES
      // match a photo's alt text, switching to a different, correct
      // photo), and this must never undo a legitimate change like that.
      // markDeliberateChoice (above) is what tells a real choice of the
      // first photo apart from the app's misfire landing on it — both
      // produce the exact same class change, but only a real choice
      // (a trusted tap, or this file's own button/swipe navigation)
      // updates that record. Capped overall so this can never fight the
      // page back and forth indefinitely if the app's own logic were
      // ever to retry repeatedly.
      var guardMediaId = lastSlide ? lastSlide.dataset.mediaId : null;
      var guardUntil = 0;
      var restoring = false;
      var restoreAttempted = false;
      var totalRestores = 0;
      var MAX_RESTORES = 3;
      var observer = new MutationObserver(function () {
        updateButtonState(gallery);
        var freshSlides = slidesFor(gallery);
        var newIndex = activeIndex(freshSlides);
        var newSlide = freshSlides[newIndex];
        if (!newSlide || newSlide === lastSlide) return;
        var newMediaId = newSlide.dataset.mediaId;

        if (restoring) {
          // The restore click above completing — treat this as the
          // legitimate current state rather than animating back to it a
          // second time, but stay alert a little longer in case
          // whatever caused this tries again.
          restoring = false;
          lastIndex = newIndex;
          lastSlide = newSlide;
          guardUntil = Date.now() + 1500;
          restoreAttempted = false;
          return;
        }

        var wasActuallyChosen = lastDeliberateMediaId.get(gallery) === newMediaId;
        if (Date.now() < guardUntil && !restoreAttempted && !wasActuallyChosen && newIndex === 0 && newMediaId !== guardMediaId && totalRestores < MAX_RESTORES) {
          var correctThumbnail = document.querySelector('.thumbnail-list__item[data-target="' + guardMediaId + '"] button.thumbnail');
          if (correctThumbnail) {
            restoring = true;
            restoreAttempted = true;
            totalRestores++;
            correctThumbnail.click();
            return;
          }
        }

        if (gallery.__wattecSkipAnim) {
          // The photo viewer closing on a different photo: the page
          // simply shows it, since the customer has just been looking at
          // it full-screen and a slide behind the viewer would be wasted.
          gallery.__wattecSkipAnim = false;
          finishSlideAnimation(gallery);
          settleGallery(gallery);
        } else {
          animateSlideChange(gallery, lastSlide, newSlide, newIndex > lastIndex ? 'next' : 'prev');
        }
        lastIndex = newIndex;
        lastSlide = newSlide;
        guardMediaId = newMediaId;
        guardUntil = Date.now() + 1500;
        restoreAttempted = false;
        totalRestores = 0;
      });
      slides.forEach(function (slide) {
        observer.observe(slide, { attributes: true, attributeFilter: ['class'] });
      });

      // Dawn's own SliderComponent (global.js) also manages this same
      // button's disabled state on its own, based on scroll position —
      // logic that never applied here in the first place (this gallery
      // doesn't actually scroll; see the top of this file), and it
      // recalculates on a resize observer that the animation above ends
      // up triggering, setting the real disabled attribute moments after
      // this sets data-atlimit — which, being a genuine disabled button
      // rather than just a faded one, is exactly what made pressing
      // previous/next stop working after the first press. Watching the
      // buttons' own disabled attribute and clearing it straight back off
      // whenever anything else sets it holds up regardless of when or how
      // often that other logic runs — clearing an already-false value is
      // a no-op, so there's no risk of the two fighting in a loop.
      ['previous', 'next'].forEach(function (name) {
        var btn = gallery.querySelector('button[name="' + name + '"]');
        if (!btn) return;
        new MutationObserver(function () { btn.disabled = false; })
          .observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      });
    });
  }

  /**
   * The thumbnail strip: a plain, natively scrolling list at every width —
   * a sideways row below 1025px, the column beside the photo from 1025px
   * up — with its own previous/next arrows driving that same scroll.
   *
   * From 1025px up the column used to be run by an older carousel plugin
   * (slick) that the theme ships, with a good deal of this file spent
   * correcting it. It could never be made to agree with anything else:
   * the mouse wheel did nothing over it, and dragging it after using its
   * arrows made it jump, because slick kept its own idea of where it was.
   * It is no longer started (see slickVeritcal in dt-theme.js); the column
   * is ordinary scrolling now, so the wheel, a trackpad, a mouse drag and
   * the arrows all move the same thing.
   */
  var THUMB_COLUMN_MIN = 1025;

  function thumbnailListFor(gallery) {
    return gallery.querySelector('.thumbnail-list');
  }

  function slickOwns(list) {
    return list.classList.contains('slick-initialized');
  }

  function thumbsVertical() {
    return window.innerWidth >= THUMB_COLUMN_MIN;
  }

  function thumbStep(list) {
    var items = list.querySelectorAll('.thumbnail-list__item');
    if (!items.length) return 0;
    if (!thumbsVertical()) return items[0].offsetWidth;
    // One thumbnail plus the gap below it.
    return items.length > 1 ? items[1].offsetTop - items[0].offsetTop : items[0].offsetHeight;
  }

  function scrollThumbnails(gallery, direction) {
    var list = thumbnailListFor(gallery);
    if (!list || slickOwns(list)) return;
    var step = thumbStep(list) * (direction === 'next' ? 1 : -1);
    if (thumbsVertical()) list.scrollBy({ top: step, behavior: 'smooth' });
    else list.scrollBy({ left: step, behavior: 'smooth' });
  }

  function updateThumbnailButtonState(gallery) {
    var list = thumbnailListFor(gallery);
    if (!list || slickOwns(list)) return;
    var prevBtn = gallery.querySelector('button[name="previous"]');
    var nextBtn = gallery.querySelector('button[name="next"]');
    // Tolerances of 2px: sub-pixel scroll positions at some zoom levels
    // stop a genuinely finished scroll a fraction short of the end.
    var atStart, atEnd, fits;
    if (thumbsVertical()) {
      atStart = list.scrollTop <= 1;
      atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 2;
      fits = list.scrollHeight <= list.clientHeight + 2;
    } else {
      atStart = list.scrollLeft <= 1;
      atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
      fits = list.scrollWidth <= list.clientWidth + 2;
    }
    if (prevBtn) prevBtn.toggleAttribute('data-atlimit', atStart);
    if (nextBtn) nextBtn.toggleAttribute('data-atlimit', atEnd);
    // Read by section-slider.css: the "more this way" fades on the desktop
    // column, and whether the column needs its arrows at all.
    gallery.toggleAttribute('data-thumbs-fit', fits);
    gallery.toggleAttribute('data-more-before', !fits && !atStart);
    gallery.toggleAttribute('data-more-after', !fits && !atEnd);
  }

  /* Brings the highlighted thumbnail into view in the desktop column,
     scrolling only as far as needed. (The row below 1025px is already
     handled by the theme's own media-gallery.js.) */
  function revealActiveThumbnail(gallery, smooth) {
    if (!thumbsVertical()) return;
    var list = thumbnailListFor(gallery);
    var button = list && list.querySelector('button.thumbnail[aria-current]');
    var item = button && button.closest('.thumbnail-list__item');
    if (!item) return;
    var top = item.getBoundingClientRect().top - list.getBoundingClientRect().top - list.clientTop + list.scrollTop;
    var target = null;
    if (top < list.scrollTop) target = top;
    else if (top + item.offsetHeight > list.scrollTop + list.clientHeight) target = top + item.offsetHeight - list.clientHeight;
    if (target !== null) list.scrollTo({ top: Math.max(0, target), behavior: smooth ? 'smooth' : 'auto' });
  }

  // Caught on the way down (capture) and stopped there, so these presses
  // are handled here alone: the theme's own slider code is also attached
  // to these two buttons, only knows sideways scrolling, and its
  // scrollTo({ left }) cancelled the column's scroll part-way through.
  document.addEventListener('click', function (event) {
    var button = event.target.closest('.GalleryThumbnails button[name="previous"], .GalleryThumbnails button[name="next"]');
    if (!button) return;
    var gallery = button.closest('slider-component');
    if (!gallery) return;
    event.stopPropagation();
    event.preventDefault();
    if (button.hasAttribute('data-atlimit')) { bounceEdge(gallery, button.name === 'next' ? 'next' : 'prev'); return; }
    scrollThumbnails(gallery, button.name === 'next' ? 'next' : 'prev');
  }, true);

  /* Click-and-drag scrolling with a mouse, which a scrolling list doesn't
     do on its own (touch screens already drag natively). A drag never
     also counts as a click on the thumbnail it started on. */
  function watchThumbnailMouseDrag(gallery) {
    var list = thumbnailListFor(gallery);
    if (!list) return;
    var drag = null;
    list.addEventListener('dragstart', function (event) { event.preventDefault(); });
    list.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, left: list.scrollLeft, top: list.scrollTop, moved: false };
    });
    list.addEventListener('pointermove', function (event) {
      if (!drag || event.pointerId !== drag.id) return;
      var dx = event.clientX - drag.x;
      var dy = event.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) > 5) {
        drag.moved = true;
        list.style.scrollBehavior = 'auto';
        list.classList.add('is-dragging');
        try { list.setPointerCapture(event.pointerId); } catch (e) { /* not capturable */ }
      }
      if (!drag.moved) return;
      event.preventDefault();
      if (thumbsVertical()) list.scrollTop = drag.top - dy;
      else list.scrollLeft = drag.left - dx;
    });
    function end(event) {
      if (!drag || event.pointerId !== drag.id) return;
      if (drag.moved) suppressNextClickGlobally();
      list.style.scrollBehavior = '';
      list.classList.remove('is-dragging');
      drag = null;
    }
    list.addEventListener('pointerup', end);
    list.addEventListener('pointercancel', end);
  }

  function watchThumbnailGalleries() {
    document.querySelectorAll('.GalleryThumbnails').forEach(function (gallery) {
      var list = thumbnailListFor(gallery);
      if (!list) return;
      var update = function () { updateThumbnailButtonState(gallery); };
      update();
      list.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      // Arrows hide when there's nowhere to go, so a list that only
      // finishes overflowing once its photos have loaded needs another look.
      window.addEventListener('load', update);
      if (window.ResizeObserver) new ResizeObserver(update).observe(list);
      watchThumbnailMouseDrag(gallery);
      // The theme's slider code also marks these buttons disabled from its
      // own sideways-only reckoning every time the list scrolls — which,
      // on the vertical column, disabled "next" while there was still more
      // below. Their real state is data-atlimit (above); a disabled
      // attribute from anywhere else is taken straight back off.
      gallery.querySelectorAll('button[name="previous"], button[name="next"]').forEach(function (btn) {
        btn.disabled = false;
        new MutationObserver(function () { if (btn.disabled) btn.disabled = false; })
          .observe(btn, { attributes: true, attributeFilter: ['disabled'] });
      });
      // Whichever way the photo changes — arrows, swipe, a thumbnail, the
      // viewer closing, a variant — the theme moves aria-current to the
      // matching thumbnail; the column follows it.
      new MutationObserver(function () { revealActiveThumbnail(gallery, true); })
        .observe(list, { attributes: true, attributeFilter: ['aria-current'], subtree: true });
    });
  }

  /**
   * Holds the main photo's previous/next arrows at one fixed height instead
   * of the middle of whichever photo is showing, which made them jump up
   * and down whenever a taller or shorter photo came up. The height used is
   * the middle of the first photo, raised only as far as needed to stay
   * over the gallery's SHORTEST photo, so the arrows sit over every photo. Each slide's proportions are already written into
   * the page (the padding-top percentage on its .product__media), so no
   * photo has to be loaded or shown to be measured. Written to
   * --wt-nav-top, which section-slider.css reads; recalculated only when
   * the gallery's width changes, since that is the only thing that changes
   * the answer.
   */
  function pinGalleryArrows(gallery) {
    var buttons = gallery.querySelector('.slider-buttons');
    if (!buttons) return;
    var lastWidth = -1;

    function pin() {
      var active = gallery.querySelector('.product__media-item.is-active .product__media') ||
        gallery.querySelector('.product__media-item .product__media');
      if (!active) return;
      var rect = active.getBoundingClientRect();
      if (!rect.width) return;
      if (Math.abs(rect.width - lastWidth) < 1) return;
      lastWidth = rect.width;

      var shortest = Infinity;
      var first = NaN;
      gallery.querySelectorAll('.product__media-item .product__media').forEach(function (media) {
        var match = /padding-top:\s*([\d.]+)%/.exec(media.getAttribute('style') || '');
        var pct = match ? parseFloat(match[1]) : NaN;
        if (!(pct > 0)) return;
        if (isNaN(first)) first = pct;
        if (pct < shortest) shortest = pct;
      });
      if (!isFinite(shortest)) return;

      // The middle of the first photo — where the arrows naturally sit on
      // arrival — unless that would put them below the bottom of the
      // shortest photo, in which case they rise just enough to stay on it.
      var arrow = gallery.querySelector('.slider-button--next');
      var clearance = (arrow ? arrow.offsetHeight / 2 : 28) + 12;
      var centre = Math.min(rect.width * first / 200, rect.width * shortest / 100 - clearance);
      centre = Math.max(centre, rect.width * shortest / 200);

      var top = rect.top - buttons.getBoundingClientRect().top;
      buttons.style.setProperty('--wt-nav-top', Math.round(top + centre) + 'px');
    }

    pin();
    window.addEventListener('load', function () { lastWidth = -1; pin(); });
    if (window.ResizeObserver) {
      new ResizeObserver(pin).observe(gallery);
    } else {
      window.addEventListener('resize', pin);
    }
  }

  function pinAllGalleryArrows() {
    document.querySelectorAll('.GalleryViewerMain').forEach(pinGalleryArrows);
  }

  /**
   * Below 1025px the thumbnail row's own previous/next buttons disappear
   * at whichever end has nowhere further to go (see section-slider.css),
   * so there is no button left to press for the "pressed, nothing there"
   * edge flash (see bounceEdge above). Instead this reads the same touch
   * gesture the row's own native scrolling is already following and, if
   * it's still being pushed further once scrollLeft has genuinely nowhere
   * left to go, plays that flash itself.
   */
  function watchThumbnailOverscroll(gallery) {
    var list = thumbnailListFor(gallery);
    if (!list) return;
    var startX = null, firedThisTouch = false;

    list.addEventListener('touchstart', function (event) {
      if (slickOwns(list) || !event.touches.length) return;
      startX = event.touches[0].clientX;
      firedThisTouch = false;
    }, { passive: true });

    list.addEventListener('touchmove', function (event) {
      if (slickOwns(list) || startX === null || firedThisTouch || !event.touches.length) return;
      var dx = event.touches[0].clientX - startX;
      var atStart = list.scrollLeft <= 1;
      var atEnd = list.scrollLeft + list.clientWidth >= list.scrollWidth - 2;
      if ((dx > 15 && atStart) || (dx < -15 && atEnd)) {
        bounceEdge(gallery, dx > 0 ? 'prev' : 'next');
        firedThisTouch = true;
      }
    }, { passive: true });
  }

  function watchThumbnailOverscrollGalleries() {
    document.querySelectorAll('.GalleryThumbnails').forEach(watchThumbnailOverscroll);
  }

  /**
   * Swipe-to-change-photo on the main image, following the finger. There's
   * no scrollable content to hand off to here (see the top of this file),
   * so this reads the pointer's own movement directly: once a drag is
   * clearly sideways, the photo moves with it and the next (or previous)
   * photo slides in beside it, so the customer can see where they're
   * going. Let go far enough — or flick — and the change completes from
   * wherever the finger left it (animateSlideChange picks up the position
   * from gallery.__wattecDrag); otherwise both glide back. At the first or
   * last photo the photo only gives a little, like an elastic band, and
   * the edge glow plays.
   *
   * Pointer events cover touch, mouse and pen in one listener, so phones
   * and tablets get a finger swipe and desktop a click-and-drag. A plain
   * tap/click is left alone so the photo viewer still opens normally; the
   * stray click that follows a drag's release is swallowed so it doesn't
   * pop the viewer open too.
   */
  function watchSwipe(gallery) {
    var mediaList = mediaListFor(gallery);
    if (!mediaList) return;

    // Bound to the whole gallery, not just the image list, because the
    // previous/next buttons sit in an absolutely-positioned wrapper that
    // covers the entire image on top of it.
    gallery.style.touchAction = 'pan-y';
    gallery.addEventListener('dragstart', function (event) { event.preventDefault(); });

    // .product__media-list still carries Dawn's own overflow-x: auto and
    // scroll-snap from the stock (side-by-side, truly scrollable) layout
    // this theme doesn't use. Left on, a swipe was also read by the browser
    // as a native scroll with nothing to scroll to, which then settled
    // back to 0 and swapped the photo back to the first one.
    mediaList.style.overflowX = 'hidden';
    mediaList.style.scrollSnapType = 'none';
    mediaList.style.touchAction = 'pan-y';

    var start = null;
    var decided = null;
    var drag = null;
    var lastX = 0, lastT = 0, velocity = 0;

    function listWidth() {
      return mediaList.getBoundingClientRect().width || gallery.clientWidth;
    }

    function unpinList() {
      mediaList.style.transition = '';
      mediaList.style.position = '';
      mediaList.style.overflow = '';
      mediaList.style.height = '';
    }

    function beginDrag() {
      finishSlideAnimation(gallery);
      var slides = slidesFor(gallery);
      var current = activeIndex(slides);
      if (current === -1) return null;
      var from = slides[current];
      var height = mediaList.getBoundingClientRect().height;
      var display = getComputedStyle(from).display;
      mediaList.style.transition = 'none';
      mediaList.style.position = 'relative';
      mediaList.style.overflow = 'hidden';
      mediaList.style.height = height + 'px';
      positionForSlide(from, display);
      gallery.__wattecDragging = true;
      gallery.__wattecAnimating = true;
      return { slides: slides, index: current, from: from, neighbour: null, dir: null, display: display, height: height, offset: 0, dx: 0 };
    }

    function moveDrag(dx) {
      var w = listWidth();
      var dir = dx < 0 ? 'next' : 'prev';
      var target = drag.slides[drag.index + (dir === 'next' ? 1 : -1)] || null;
      if (drag.neighbour && drag.neighbour !== target) {
        clearSlideStyles(drag.neighbour);
        drag.neighbour = null;
      }
      if (target && !drag.neighbour) {
        positionForSlide(target, drag.display);
        drag.neighbour = target;
      }
      drag.dir = dir;
      drag.dx = dx;
      drag.offset = target ? dx : dx * 0.25;
      drag.from.style.transform = 'translate3d(' + drag.offset + 'px,0,0)';
      if (target) target.style.transform = 'translate3d(' + ((dir === 'next' ? w : -w) + drag.offset) + 'px,0,0)';
    }

    // Glides a drag back to where it started.
    function releaseDrag(d) {
      gallery.__wattecDragging = false;
      if (!d) return;
      var w = listWidth();
      var transition = 'transform 240ms ' + SLIDE_EASE;
      d.from.style.transition = transition;
      d.from.style.transform = 'translate3d(0,0,0)';
      if (d.neighbour) {
        d.neighbour.style.transition = transition;
        d.neighbour.style.transform = 'translate3d(' + (d.dir === 'next' ? w : -w) + 'px,0,0)';
      }
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        if (gallery.__wattecFinishAnim === finish) gallery.__wattecFinishAnim = null;
        clearSlideStyles(d.from);
        clearSlideStyles(d.neighbour);
        unpinList();
        settleGallery(gallery);
      }
      gallery.__wattecFinishAnim = finish;
      setTimeout(finish, 280);
    }

    function commitDrag(d) {
      gallery.__wattecDragging = false;
      var handoff = {
        offset: d.offset,
        display: d.display,
        height: d.height,
        neighbour: d.neighbour,
        release: function () { releaseDrag(d); }
      };
      gallery.__wattecDrag = handoff;
      var moved = goToAdjacentSlide(gallery, d.dir);
      // If the change didn't go through (or was absorbed without a slide),
      // nothing will have picked the drag up — put it back.
      setTimeout(function () {
        if (gallery.__wattecDrag === handoff) {
          gallery.__wattecDrag = null;
          releaseDrag(d);
        }
      }, moved ? 60 : 0);
    }

    gallery.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('button')) return;
      // With a mouse, stops the browser starting a text selection or
      // dragging the photo's own link while the photo is being swiped.
      if (event.pointerType === 'mouse') event.preventDefault();
      start = { x: event.clientX, y: event.clientY, id: event.pointerId, mouse: event.pointerType === 'mouse' };
      decided = null;
      lastX = event.clientX;
      lastT = event.timeStamp;
      velocity = 0;
    });

    gallery.addEventListener('pointermove', function (event) {
      if (!start || event.pointerId !== start.id) return;
      var dx = event.clientX - start.x;
      var dy = event.clientY - start.y;
      if (!decided && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
        // A mouse can't scroll the page by dragging, so any mouse drag is
        // a swipe. (Deciding "vertical" from a slightly downward start used
        // to leave the photo stuck, then open the viewer on release.) On a
        // touch screen an up/down drag is the page scrolling.
        decided = start.mouse || Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
        if (decided === 'horizontal') {
          drag = beginDrag();
          try { gallery.setPointerCapture(event.pointerId); } catch (e) { /* not capturable */ }
        }
      }
      if (decided !== 'horizontal' || !drag) return;
      event.preventDefault();
      var dt = event.timeStamp - lastT;
      if (dt > 0) velocity = 0.7 * ((event.clientX - lastX) / dt) + 0.3 * velocity;
      lastX = event.clientX;
      lastT = event.timeStamp;
      moveDrag(dx);
    });

    function end(event) {
      if (!start || event.pointerId !== start.id) return;
      var wasHorizontal = decided === 'horizontal';
      var movedAtAll = decided !== null;
      start = null;
      decided = null;
      var d = drag;
      drag = null;
      if (!wasHorizontal || !d) {
        // Any drag, whichever way, is not a click on the photo.
        if (movedAtAll) suppressNextClickGlobally();
        return;
      }
      if (event.type === 'pointercancel') {
        releaseDrag(d);
        return;
      }
      var farEnough = Math.abs(d.dx) >= Math.max(40, listWidth() * 0.18);
      var flicked = Math.abs(d.dx) > 25 && Math.abs(velocity) > 0.35 && (velocity < 0) === (d.dx < 0);
      if (d.neighbour && (farEnough || flicked)) {
        commitDrag(d);
      } else {
        releaseDrag(d);
        if (!d.neighbour && Math.abs(d.dx) >= 40) bounceImageEdge(gallery, d.dir);
      }
      // A real drag happened, so its trailing click mustn't also open the
      // photo viewer. (goToAdjacentSlide's own click is script-made and is
      // never swallowed.)
      suppressNextClickGlobally();
    }

    gallery.addEventListener('pointerup', end);
    gallery.addEventListener('pointercancel', end);
  }

  function watchSwipeGalleries() {
    document.querySelectorAll('.GalleryViewerMain').forEach(watchSwipe);
  }

  /**
   * The full-screen photo viewer, on both desktop and touch screens. It
   * replaces the theme's own click-to-zoom (PhotoSwipe, or a
   * <modal-opener> dialog, depending on a setting — neither opened
   * reliably here), and this rebuild replaces a first version of it that
   * swapped the picture in a single <img>: every press of next/previous
   * waited for a full-size download (about two seconds each on a fast
   * connection) with the old photo still showing, and each press also
   * drove the product page's own slide animation behind it.
   *
   * Now:
   * - Every photo sits on one strip that slides, and follows a finger
   *   while it's dragged; presses in quick succession just retarget it.
   * - A photo already shown on the page appears instantly at that size,
   *   and is quietly replaced by the full-size version once that has
   *   downloaded; the photos either side are fetched ahead of time.
   * - Zoom: the mouse wheel (towards the pointer), the +/- buttons, a
   *   click on the photo, and on touch screens pinch and double-tap.
   *   Once zoomed, dragging moves around the photo instead of changing
   *   it.
   * - On touch screens a swipe up or down closes it, and on every device
   *   the browser's Back button closes it rather than leaving the page.
   * - It keeps its own place, and only moves the product page's photo to
   *   match when it closes.
   */
  var LB_MAX_ZOOM = 5;
  var LB_STEP = 1.6;
  var LB_TAP_ZOOM = 2.5;
  var lb = null;
  var lbTapTimer = null;

  var LB_ICONS = {
    minus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>',
    plus: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" fill="none"/></svg>'
  };

  function withWidth(url, width) {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&]width=)\d+/, '$1' + width);
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function lightboxItems(gallery) {
    // Large enough to stay sharp when zoomed; a little smaller on phones.
    var full = window.innerWidth < 750 ? 2048 : 2489;
    return slidesFor(gallery).map(function (slide) {
      var img = slide.querySelector('modal-opener img') || slide.querySelector('img');
      var link = slide.querySelector('modal-opener a[href]');
      var source = link ? link.href : (img ? (img.getAttribute('data-zoom') || img.currentSrc || img.src) : '');
      // First look: the very file the page is showing (or already
      // fetching) for this photo, which costs nothing extra; for a photo
      // the page hasn't started on, a medium size that arrives quickly.
      var preview = (img && img.currentSrc) || withWidth(source, 1100);
      var w = img ? parseFloat(img.getAttribute('width')) || img.naturalWidth : 0;
      var h = img ? parseFloat(img.getAttribute('height')) || img.naturalHeight : 0;
      return {
        mediaId: slide.dataset.mediaId,
        preview: preview,
        full: withWidth(source, full),
        alt: img ? img.alt : '',
        ratio: w && h ? w / h : 0
      };
    });
  }

  function buildLightbox() {
    if (lb) return lb;
    var root = document.createElement('div');
    root.className = 'wattec-lightbox';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Product photos');
    root.innerHTML =
      '<div class="wattec-lightbox__viewport"><div class="wattec-lightbox__track"></div></div>' +
      '<div class="wattec-lightbox__bar">' +
        '<span class="wattec-lightbox__counter" aria-live="polite"></span>' +
        '<div class="wattec-lightbox__tools">' +
          '<button type="button" class="wattec-lightbox__tool" data-lb="zoom-out" aria-label="Zoom out">' + LB_ICONS.minus + '</button>' +
          '<button type="button" class="wattec-lightbox__tool" data-lb="zoom-in" aria-label="Zoom in">' + LB_ICONS.plus + '</button>' +
          '<button type="button" class="wattec-lightbox__tool" data-lb="close" aria-label="Close">' + LB_ICONS.close + '</button>' +
        '</div>' +
      '</div>' +
      '<button type="button" class="wattec-lightbox__nav wattec-lightbox__prev" data-lb="prev" aria-label="Previous photo"></button>' +
      '<button type="button" class="wattec-lightbox__nav wattec-lightbox__next" data-lb="next" aria-label="Next photo"></button>';
    document.body.appendChild(root);

    lb = {
      root: root,
      viewport: root.querySelector('.wattec-lightbox__viewport'),
      track: root.querySelector('.wattec-lightbox__track'),
      counter: root.querySelector('.wattec-lightbox__counter'),
      zoomOutBtn: root.querySelector('[data-lb="zoom-out"]'),
      zoomInBtn: root.querySelector('[data-lb="zoom-in"]'),
      closeBtn: root.querySelector('[data-lb="close"]'),
      prevBtn: root.querySelector('[data-lb="prev"]'),
      nextBtn: root.querySelector('[data-lb="next"]'),
      gallery: null,
      items: [],
      slides: [],
      index: 0,
      scale: 1,
      tx: 0,
      ty: 0,
      base: null,
      pushed: false,
      closing: false
    };

    root.addEventListener('click', function (event) {
      var button = event.target.closest('[data-lb]');
      if (!button) return;
      event.stopPropagation();
      switch (button.dataset.lb) {
        case 'prev': lbGo(lb.index - 1); break;
        case 'next': lbGo(lb.index + 1); break;
        case 'zoom-in': lbZoomStep(LB_STEP); break;
        case 'zoom-out': lbZoomStep(1 / LB_STEP); break;
        case 'close': closeLightbox(); break;
      }
    });

    root.addEventListener('wheel', function (event) {
      if (root.hidden) return;
      event.preventDefault();
      var dy = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? 400 : 1);
      // A trackpad pinch arrives as a wheel with ctrlKey, in smaller steps.
      var factor = Math.exp(-dy * (event.ctrlKey ? 0.01 : 0.0022));
      lbZoomAt(event.clientX, event.clientY, lb.scale * factor, false);
    }, { passive: false });

    document.addEventListener('keydown', function (event) {
      if (!lb || lb.root.hidden) return;
      switch (event.key) {
        case 'Escape': closeLightbox(); break;
        case 'ArrowLeft': lbGo(lb.index - 1); break;
        case 'ArrowRight': lbGo(lb.index + 1); break;
        case '+': case '=': lbZoomStep(LB_STEP); break;
        case '-': case '_': lbZoomStep(1 / LB_STEP); break;
        case '0': lbResetZoom(true); break;
        case 'Tab': lbTrapFocus(event); return;
        default: return;
      }
      event.preventDefault();
    });

    window.addEventListener('resize', function () {
      if (!lb || lb.root.hidden) return;
      lbResetZoom(false);
      lbFit();
      lbPlaceTrack(false);
    });

    window.addEventListener('popstate', function () {
      if (lb && !lb.root.hidden && !lb.closing) {
        lb.pushed = false;
        closeLightbox(true);
      }
    });

    lbWireGestures();
    return lb;
  }

  function lbTrapFocus(event) {
    var focusable = Array.from(lb.root.querySelectorAll('button')).filter(function (b) {
      return !b.disabled && !b.hidden && b.offsetParent !== null;
    });
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { last.focus(); event.preventDefault(); }
    else if (!event.shiftKey && document.activeElement === last) { first.focus(); event.preventDefault(); }
    else if (!lb.root.contains(document.activeElement)) { first.focus(); event.preventDefault(); }
  }

  function lbRenderSlides() {
    lb.track.textContent = '';
    lb.slides = lb.items.map(function (item) {
      var el = document.createElement('div');
      el.className = 'wattec-lightbox__slide';
      var layer = document.createElement('div');
      layer.className = 'wattec-lightbox__zoom';
      var img = document.createElement('img');
      img.className = 'wattec-lightbox__image';
      img.alt = item.alt;
      img.draggable = false;
      img.decoding = 'async';
      layer.appendChild(img);
      el.appendChild(layer);
      lb.track.appendChild(el);
      return { el: el, layer: layer, img: img, item: item, started: false };
    });
  }

  /* Each photo's frame is sized from its known proportions before it has
     loaded, so a photo still arriving already occupies exactly the space
     it will fill — nothing jumps when it lands, and zoom works on it
     straight away. */
  function lbFitSlide(slide, boxW, boxH) {
    var ratio = slide.item.ratio || 1;
    var w = Math.min(boxW, boxH * ratio);
    slide.img.style.width = Math.round(w) + 'px';
    slide.img.style.height = Math.round(w / ratio) + 'px';
  }

  function lbFit() {
    var first = lb.slides[0];
    if (!first) return;
    var boxW = first.layer.clientWidth;
    var boxH = first.layer.clientHeight;
    lb.slides.forEach(function (slide) { lbFitSlide(slide, boxW, boxH); });
  }

  function lbLoad(index) {
    var slide = lb.slides[index];
    if (!slide || slide.started) return;
    slide.started = true;
    var item = slide.item;
    var shown = false;
    slide.el.classList.add('is-loading');
    slide.img.onload = slide.img.onerror = function () {
      shown = true;
      slide.el.classList.remove('is-loading');
      if (!item.ratio && slide.img.naturalWidth) {
        item.ratio = slide.img.naturalWidth / slide.img.naturalHeight;
        lbFitSlide(slide, slide.layer.clientWidth, slide.layer.clientHeight);
      }
    };
    slide.img.src = item.preview || item.full;
    if (!item.full || item.full === slide.img.getAttribute('src')) return;
    // The sharp version, swapped in only once it is fully decoded so the
    // swap itself is invisible.
    var hi = new Image();
    hi.decoding = 'async';
    hi.onload = function () {
      var swap = function () { slide.img.src = item.full; };
      if (hi.decode) hi.decode().then(swap, swap); else swap();
    };
    hi.onerror = function () {
      if (!shown) slide.el.classList.remove('is-loading');
    };
    hi.src = item.full;
  }

  function lbWidth() {
    return lb.viewport.clientWidth || window.innerWidth;
  }

  function lbPlaceTrack(animate, extra) {
    var x = -lb.index * lbWidth() + (extra || 0);
    lb.track.style.transition = animate ? 'transform 320ms ' + SLIDE_EASE : 'none';
    lb.track.style.transform = 'translate3d(' + x + 'px,0,0)';
  }

  function lbUpdateChrome() {
    var count = lb.items.length;
    lb.counter.textContent = (lb.index + 1) + ' / ' + count;
    lb.prevBtn.hidden = lb.nextBtn.hidden = count < 2;
    lb.prevBtn.toggleAttribute('data-atlimit', lb.index <= 0);
    lb.nextBtn.toggleAttribute('data-atlimit', lb.index >= count - 1);
    lb.zoomOutBtn.disabled = lb.scale <= 1.001;
    lb.zoomInBtn.disabled = lb.scale >= LB_MAX_ZOOM - 0.001;
    lb.root.classList.toggle('is-zoomed', lb.scale > 1.001);
  }

  // A small elastic nudge at either end, so a press there still registers.
  function lbNudge(sign) {
    lb.track.style.transition = 'transform 120ms ease-out';
    lb.track.style.transform = 'translate3d(' + (-lb.index * lbWidth() + sign * 36) + 'px,0,0)';
    setTimeout(function () { lbPlaceTrack(true); }, 120);
  }

  function lbGo(index) {
    if (!lb.items.length) return;
    if (index < 0 || index >= lb.items.length) {
      lbNudge(index < 0 ? 1 : -1);
      return;
    }
    if (index !== lb.index) lbResetZoom(false);
    lb.index = index;
    lbPlaceTrack(true);
    lbLoad(index);
    lbLoad(index + 1);
    lbLoad(index - 1);
    lbUpdateChrome();
  }

  function lbCurrent() {
    return lb.slides[lb.index];
  }

  /* Measured with the photo at its normal size: where the zoom layer's
     centre is (the point zooming scales around) and how big the photo and
     the space around it are, which is what keeps a zoomed photo from
     being dragged off screen. */
  function lbMeasure() {
    var slide = lbCurrent();
    if (!slide) return null;
    var saved = slide.layer.style.transform;
    slide.layer.style.transform = 'none';
    var layer = slide.layer.getBoundingClientRect();
    var img = slide.img.getBoundingClientRect();
    var frame = slide.el.getBoundingClientRect();
    slide.layer.style.transform = saved;
    return {
      cx: layer.left + layer.width / 2,
      cy: layer.top + layer.height / 2,
      w: img.width,
      h: img.height,
      left: frame.left,
      right: frame.right,
      top: frame.top,
      bottom: frame.bottom
    };
  }

  function lbClamp(scale, tx, ty) {
    var b = lb.base;
    var w = b.w * scale;
    var h = b.h * scale;
    tx = w <= b.right - b.left ? 0 : Math.min(b.left - b.cx + w / 2, Math.max(b.right - b.cx - w / 2, tx));
    ty = h <= b.bottom - b.top ? 0 : Math.min(b.top - b.cy + h / 2, Math.max(b.bottom - b.cy - h / 2, ty));
    return [tx, ty];
  }

  function lbApplyZoom(scale, tx, ty, animate) {
    var slide = lbCurrent();
    if (!slide) return;
    if (!lb.base) lb.base = lbMeasure();
    if (!lb.base || !lb.base.w) return;
    scale = Math.max(1, Math.min(LB_MAX_ZOOM, scale));
    var t = lbClamp(scale, tx, ty);
    lb.scale = scale;
    lb.tx = t[0];
    lb.ty = t[1];
    slide.layer.classList.toggle('is-animating', !!animate);
    if (animate) {
      clearTimeout(slide.animTimer);
      slide.animTimer = setTimeout(function () { slide.layer.classList.remove('is-animating'); }, 260);
    }
    slide.layer.style.transform = scale <= 1.001
      ? ''
      : 'translate3d(' + lb.tx + 'px,' + lb.ty + 'px,0) scale(' + scale + ')';
    if (scale <= 1.001) {
      lb.scale = 1;
      lb.tx = lb.ty = 0;
      lb.base = null;
    }
    lbUpdateChrome();
  }

  // Zooms so the point under (x, y) on screen stays where it is.
  function lbZoomAt(x, y, scale, animate) {
    if (!lb.base) lb.base = lbMeasure();
    if (!lb.base) return;
    scale = Math.max(1, Math.min(LB_MAX_ZOOM, scale));
    var qx = x - lb.base.cx;
    var qy = y - lb.base.cy;
    var k = scale / lb.scale;
    lbApplyZoom(scale, qx - k * (qx - lb.tx), qy - k * (qy - lb.ty), animate);
  }

  function lbZoomStep(factor) {
    var rect = lb.viewport.getBoundingClientRect();
    var next = lb.scale * factor;
    if (factor < 1 && next < 1.05) { lbResetZoom(true); return; }
    lbZoomAt(rect.left + rect.width / 2, rect.top + rect.height / 2, next, true);
  }

  function lbResetZoom(animate) {
    if (!lb || (lb.scale <= 1.001 && !lb.tx && !lb.ty)) return;
    var slide = lbCurrent();
    lb.scale = 1;
    lb.tx = lb.ty = 0;
    lb.base = null;
    if (slide) {
      slide.layer.classList.toggle('is-animating', !!animate);
      slide.layer.style.transform = '';
      if (animate) setTimeout(function () { slide.layer.classList.remove('is-animating'); }, 260);
    }
    lbUpdateChrome();
  }

  function lbDismissOffset(dy) {
    var slide = lbCurrent();
    if (slide) {
      slide.el.style.transition = 'none';
      slide.el.style.transform = dy ? 'translate3d(0,' + dy + 'px,0)' : '';
    }
    lb.root.style.backgroundColor = dy ? 'rgba(10, 11, 10, ' + (0.94 * Math.max(0.2, 1 - Math.abs(dy) / 400)) + ')' : '';
  }

  function lbDismissBack() {
    var slide = lbCurrent();
    if (slide) {
      slide.el.style.transition = 'transform 220ms ' + SLIDE_EASE;
      slide.el.style.transform = '';
    }
    lb.root.style.backgroundColor = '';
  }

  function lbWireGestures() {
    var viewport = lb.viewport;
    var points = new Map();
    var g = null;
    var lastTap = { t: 0, x: 0, y: 0 };

    function startSingle(id, x, y, t) {
      g = { type: 'single', id: id, x0: x, y0: y, lastX: x, lastY: y, lastT: t, vx: 0, vy: 0, tx0: lb.tx, ty0: lb.ty, moved: false, axis: null };
      if (lb.scale > 1.001) lb.root.classList.add('is-panning');
    }

    function startPinch() {
      var p = Array.from(points.values());
      if (!lb.base) lb.base = lbMeasure();
      if (!lb.base) return;
      lbPlaceTrack(true);
      lbDismissOffset(0);
      g = {
        type: 'pinch',
        dist0: Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y) || 1,
        mid0: { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2 },
        scale0: lb.scale,
        tx0: lb.tx,
        ty0: lb.ty
      };
    }

    // Whether a point is on the photo itself. Worked out from position
    // rather than from what was clicked, because the viewer holds on to
    // the pointer while it's down, so the browser reports every release
    // as landing on the viewer rather than on the photo.
    function isOnImage(x, y) {
      var slide = lbCurrent();
      if (!slide) return false;
      var r = slide.img.getBoundingClientRect();
      return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
    }

    function handleTap(event) {
      var onImage = isOnImage(event.clientX, event.clientY);
      if (event.pointerType === 'mouse') {
        // Desktop: a click on the photo zooms in there, or back out; a
        // click on the dark surround closes (or first un-zooms).
        if (onImage) {
          if (lb.scale > 1.001) lbResetZoom(true); else lbZoomAt(event.clientX, event.clientY, LB_TAP_ZOOM, true);
        } else if (lb.scale > 1.001) {
          lbResetZoom(true);
        } else {
          closeLightbox();
        }
        return;
      }
      // Touch: double-tap zooms in or out; a single tap on the surround
      // closes, held back a moment so the first tap of a double-tap isn't
      // taken for it.
      var isDouble = event.timeStamp - lastTap.t < 320 && Math.hypot(event.clientX - lastTap.x, event.clientY - lastTap.y) < 40;
      clearTimeout(lbTapTimer);
      if (isDouble) {
        lastTap.t = 0;
        if (lb.scale > 1.001) lbResetZoom(true); else lbZoomAt(event.clientX, event.clientY, LB_TAP_ZOOM, true);
        return;
      }
      lastTap = { t: event.timeStamp, x: event.clientX, y: event.clientY };
      if (!onImage && lb.scale <= 1.001) lbTapTimer = setTimeout(closeLightbox, 300);
    }

    viewport.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      try { viewport.setPointerCapture(event.pointerId); } catch (e) { /* not capturable */ }
      points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (points.size === 2) startPinch();
      else if (points.size === 1) startSingle(event.pointerId, event.clientX, event.clientY, event.timeStamp);
    });

    viewport.addEventListener('pointermove', function (event) {
      if (!points.has(event.pointerId)) return;
      points.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (!g) return;

      if (g.type === 'pinch') {
        if (points.size < 2) return;
        var p = Array.from(points.values());
        var dist = Math.hypot(p[1].x - p[0].x, p[1].y - p[0].y);
        var mid = { x: (p[0].x + p[1].x) / 2, y: (p[0].y + p[1].y) / 2 };
        var scale = Math.max(1, Math.min(LB_MAX_ZOOM, g.scale0 * dist / g.dist0));
        // Pinching all the way back to normal size clears the measurement;
        // the layout it came from hasn't changed, so it's simply retaken.
        if (!lb.base) lb.base = lbMeasure();
        if (!lb.base) return;
        // Keep the spot that started under the fingers under them.
        var px = (g.mid0.x - lb.base.cx - g.tx0) / g.scale0;
        var py = (g.mid0.y - lb.base.cy - g.ty0) / g.scale0;
        lbApplyZoom(scale, mid.x - lb.base.cx - scale * px, mid.y - lb.base.cy - scale * py, false);
        return;
      }

      if (event.pointerId !== g.id) return;
      var dx = event.clientX - g.x0;
      var dy = event.clientY - g.y0;
      var dt = event.timeStamp - g.lastT;
      if (dt > 0) {
        g.vx = 0.7 * ((event.clientX - g.lastX) / dt) + 0.3 * g.vx;
        g.vy = 0.7 * ((event.clientY - g.lastY) / dt) + 0.3 * g.vy;
      }
      g.lastX = event.clientX;
      g.lastY = event.clientY;
      g.lastT = event.timeStamp;
      if (!g.moved && Math.hypot(dx, dy) > 6) g.moved = true;
      if (!g.moved) return;

      if (lb.scale > 1.001) {
        if (!lb.base) lb.base = lbMeasure();
        lbApplyZoom(lb.scale, g.tx0 + dx, g.ty0 + dy, false);
        return;
      }
      // A mouse drag is always a sideways swipe (up/down with a finger is
      // swipe-to-close, which a mouse has no use for).
      if (!g.axis) g.axis = event.pointerType === 'mouse' || Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
      if (g.axis === 'x') {
        var atEdge = (lb.index === 0 && dx > 0) || (lb.index === lb.items.length - 1 && dx < 0);
        lbPlaceTrack(false, atEdge ? dx * 0.3 : dx);
      } else if (event.pointerType !== 'mouse') {
        lbDismissOffset(dy);
      }
    });

    function end(event) {
      if (!points.has(event.pointerId)) return;
      points.delete(event.pointerId);
      if (!g) return;

      if (g.type === 'pinch') {
        if (points.size === 1) {
          // One finger still down: carry on as a drag around the photo.
          var rest = Array.from(points.entries())[0];
          startSingle(rest[0], rest[1].x, rest[1].y, event.timeStamp);
          g.moved = true;
        } else {
          g = null;
        }
        if (lb.scale < 1.03) lbResetZoom(true);
        return;
      }

      if (event.pointerId !== g.id) return;
      var gesture = g;
      g = null;
      lb.root.classList.remove('is-panning');

      if (event.type === 'pointercancel') {
        lbPlaceTrack(true);
        lbDismissBack();
        return;
      }
      if (!gesture.moved) {
        handleTap(event);
        return;
      }
      if (lb.scale > 1.001) return;

      var dx = event.clientX - gesture.x0;
      var dy = event.clientY - gesture.y0;
      if (gesture.axis === 'x') {
        var step = 0;
        var farEnough = Math.abs(dx) > lbWidth() * 0.18;
        var flicked = Math.abs(dx) > 25 && Math.abs(gesture.vx) > 0.35 && (gesture.vx < 0) === (dx < 0);
        if (farEnough || flicked) step = dx < 0 ? 1 : -1;
        var target = lb.index + step;
        if (step && target >= 0 && target < lb.items.length) lbGo(target);
        else lbPlaceTrack(true);
      } else if (gesture.axis === 'y') {
        if (Math.abs(dy) > 110 || Math.abs(gesture.vy) > 0.6) closeLightbox();
        else lbDismissBack();
      }
    }

    viewport.addEventListener('pointerup', end);
    viewport.addEventListener('pointercancel', end);
  }

  function openLightbox(gallery, startIndex) {
    buildLightbox();
    if (!lb.root.hidden) return;
    lb.gallery = gallery;
    lb.items = lightboxItems(gallery);
    if (!lb.items.length) return;
    lbRenderSlides();
    lb.index = Math.max(0, Math.min(lb.items.length - 1, startIndex || 0));
    lb.scale = 1;
    lb.tx = lb.ty = 0;
    lb.base = null;
    lb.closing = false;
    lb.root.style.backgroundColor = '';
    lb.returnFocus = document.activeElement;

    lb.root.hidden = false;
    document.documentElement.classList.add('wattec-lightbox-open');
    document.body.classList.add('wattec-lightbox-open');
    lbFit();
    lbPlaceTrack(false);
    lbLoad(lb.index);
    lbLoad(lb.index + 1);
    lbLoad(lb.index - 1);
    lbUpdateChrome();
    requestAnimationFrame(function () { lb.root.classList.add('is-open'); });
    try { lb.closeBtn.focus({ preventScroll: true }); } catch (e) { lb.closeBtn.focus(); }

    // So the phone's Back button closes the viewer instead of the page.
    try {
      history.pushState({ wattecLightbox: true }, '');
      lb.pushed = true;
    } catch (e) {
      lb.pushed = false;
    }
  }

  // Leaves the product page showing the photo the customer was last on.
  function lbSyncGallery() {
    var gallery = lb.gallery;
    var item = lb.items[lb.index];
    if (!gallery || !item) return;
    var slides = slidesFor(gallery);
    var current = slides[activeIndex(slides)];
    if (current && current.dataset.mediaId === item.mediaId) return;
    var thumbnailButton = document.querySelector('.thumbnail-list__item[data-target="' + item.mediaId + '"] button.thumbnail');
    if (!thumbnailButton) return;
    gallery.__wattecSkipAnim = true;
    markDeliberateChoice(gallery, item.mediaId);
    thumbnailButton.click();
    setTimeout(function () { gallery.__wattecSkipAnim = false; }, 100);
  }

  function closeLightbox(fromHistory) {
    if (!lb || lb.root.hidden || lb.closing) return;
    lb.closing = true;
    clearTimeout(lbTapTimer);
    lbSyncGallery();
    lb.root.classList.remove('is-open');
    setTimeout(function () {
      lb.root.hidden = true;
      lb.closing = false;
      lb.track.textContent = '';
      lb.slides = [];
      lb.root.classList.remove('is-zoomed', 'is-panning');
      document.documentElement.classList.remove('wattec-lightbox-open');
      document.body.classList.remove('wattec-lightbox-open');
    }, 200);
    if (lb.pushed && !fromHistory) {
      lb.pushed = false;
      history.back();
    }
    if (lb.returnFocus && lb.returnFocus.focus) {
      try { lb.returnFocus.focus({ preventScroll: true }); } catch (e) { /* gone */ }
    }
  }

  function watchLightboxGalleries() {
    document.querySelectorAll('.GalleryViewerMain').forEach(function (gallery) {
      // A plain tap/click that wasn't a drag (watchSwipe swallows a drag's
      // trailing click) opens the viewer on the photo showing. The photo
      // is wrapped in a link to the full-size file, which must not also
      // open in a new tab.
      gallery.addEventListener('click', function (event) {
        if (event.target.closest('button, .skip-to-content-link, deferred-media, product-model, video, iframe')) return;
        event.preventDefault();
        openLightbox(gallery, Math.max(0, activeIndex(slidesFor(gallery))));
      });
    });
  }

  /**
   * From 1025px up, the thumbnail column is meant to be exactly as tall as
   * the main photo, however many thumbnails that ends up fitting (the rest
   * scroll, with the fades in section-slider.css showing there's more).
   * Letting CSS alone stretch the column to match — align-items: stretch —
   * runs both ways: on a product with enough photos the column could end
   * up the taller side instead, pulling the photo (and its arrows) down to
   * match. Measuring the photo here and setting the column's height from
   * that, in one direction only, avoids that.
   */
  function syncThumbnailColumnHeight(gallery) {
    if (!thumbsVertical()) {
      gallery.style.height = '';
      return;
    }
    var mediaGallery = gallery.closest('media-gallery');
    var mainViewer = mediaGallery && mediaGallery.querySelector('.GalleryViewerMain');
    if (!mainViewer) return;
    // Mid-slide the photo's frame is gliding between two heights; resizing
    // the column on every one of those frames made the thumbnails jitter.
    // It is done once, when the slide settles (onGallerySettle below).
    if (mainViewer.__wattecAnimating) return;
    var targetHeight = mainViewer.getBoundingClientRect().height;
    if (!targetHeight) return;
    // Setting the column's height can nudge the photo's by a sub-pixel,
    // which the ResizeObserver below would see as another resize; already
    // right (to within rounding) means nothing to do, which stops that loop.
    if (Math.abs(parseFloat(gallery.style.height) - targetHeight) < 1) return;
    gallery.style.height = targetHeight + 'px';
    updateThumbnailButtonState(gallery);
  }

  function watchThumbnailColumnHeight() {
    document.querySelectorAll('.GalleryThumbnails').forEach(function (gallery) {
      var mediaGallery = gallery.closest('media-gallery');
      var mainViewer = mediaGallery && mediaGallery.querySelector('.GalleryViewerMain');
      if (!mainViewer) return;
      var sync = function () { syncThumbnailColumnHeight(gallery); };
      sync();
      onGallerySettle(mainViewer, sync);
      // Catches every reason the photo's height could change — window
      // resize, a photo loading in, a variant swap — in one place.
      if (window.ResizeObserver) {
        new ResizeObserver(sync).observe(mainViewer);
      } else {
        window.addEventListener('resize', sync);
      }
      window.addEventListener('load', sync);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      watchGalleries();
      watchThumbnailGalleries();
      watchThumbnailOverscrollGalleries();
      watchSwipeGalleries();
      watchThumbnailColumnHeight();
      watchLightboxGalleries();
      pinAllGalleryArrows();
    });
  } else {
    watchGalleries();
    watchThumbnailGalleries();
    watchThumbnailOverscrollGalleries();
    watchSwipeGalleries();
    watchThumbnailColumnHeight();
    watchLightboxGalleries();
    pinAllGalleryArrows();
  }
})();
